// src/components/ShowcaseFB.jsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  Image as ImageIcon,
  Sparkles,
  Layers,
  Crown,
  Box,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";

const slides = [
  /*{
    type: "video",
    id: "trailer",
    title: "Float Block – Official Trailer",
    src: "/assets/videos/demo2.mp4",
    poster: "/assets/images/game1.jpg",
    youtubeUrl: "https://www.youtube.com/watch?v=b9KWLkG8MOc",
  },*/
  {
    type: "image",
    id: "screen1",
    title: "Purple World - Level 8",
    src: "/assets/images/purpleworld.png",
  },
  {
    type: "image",
    id: "screen2",
    title: "Red World - Level 30",
    src: "/assets/images/redworld.png",
  },
  {
    type: "image",
    id: "screen3",
    title: "Blue World - Level 51",
    src: "/assets/images/blueworld.png",
  },
  {
    type: "image",
    id: "screen4",
    title: "Yellow World - Level 79",
    src: "/assets/images/yellowworld.png",
  },
  {
    type: "image",
    id: "screen5",
    title: "Cyan World - Level 91",
    src: "/assets/images/cyanworld.png",
  },
  {
    type: "image",
    id: "screen6",
    title: "Green World - Level 111",
    src: "/assets/images/greenworld.png",
  },
  {
    type: "image",
    id: "screen7",
    title: "Dark S1 World - Level 131",
    src: "/assets/images/darks1world.png",
  },
  {
    type: "image",
    id: "screen8",
    title: "Dark S2 World - Level 151",
    src: "/assets/images/darks2world.png",
  },
  {
    type: "image",
    id: "screen9",
    title: "Dark S3 World - Level 177",
    src: "/assets/images/darks3world.png",
  },
  {
    type: "image",
    id: "screen10",
    title: "Dark S4 World - Level 187",
    src: "/assets/images/darks4world.png",
  },
  {
    type: "image",
    id: "screen11",
    title: "Final Boss - Start",
    src: "/assets/images/finalboss1.png",
  },
  {
    type: "image",
    id: "screen12",
    title: "Final Boss - Battle",
    src: "/assets/images/finalboss2.png",
  },
  {
    type: "image",
    id: "screen13",
    title: "Player vs Bot - In Game",
    src: "/assets/images/multiplayer2.png",
  },
  {
    type: "image",
    id: "screen14",
    title: "Player vs Bot - Menu",
    src: "/assets/images/multiplayer1.png",
  },
  {
    type: "image",
    id: "screen15",
    title: "Main Menu",
    src: "/assets/images/mainmenu1.png",
  },
  {
    type: "image",
    id: "screen16",
    title: "Spectrum Box",
    src: "/assets/images/spectrumbox1.png",
  },
];

const IMAGE_AUTOPLAY_MS = 5000;
const VIDEO_AFTER_END_MS = 5 * 60 * 1000;

const slideVariants = {
  enter: () => ({
    opacity: 0,
    scale: 1.02,
    filter: "blur(6px)",
  }),
  center: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
  },
  exit: () => ({
    opacity: 0,
    scale: 0.98,
    filter: "blur(6px)",
  }),
};

export default function ShowcaseFB() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.6);

  const [showVideoUI, setShowVideoUI] = useState(true);
  const inactivityTimerRef = useRef(null);

  const activeSlide = slides[current];

  const [pillarsPage, setPillarsPage] = useState(0);
  const [pillarsTimerKey, setPillarsTimerKey] = useState(0); 
  const videoEndTimeoutRef = useRef(null);

  const thumbsContainerRef = useRef(null);
  const thumbsRefs = useRef([]);

  const lastInteractionRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setPillarsPage((prev) => (prev === 0 ? 1 : 0));
    }, 7000);

    return () => clearInterval(id);
  }, [pillarsTimerKey]); 

  const clearVideoEndTimeout = () => {
    if (videoEndTimeoutRef.current) {
      clearTimeout(videoEndTimeoutRef.current);
      videoEndTimeoutRef.current = null;
    }
  };

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const startInactivityTimer = React.useCallback(() => {
    clearInactivityTimer();
    if (!videoRef.current || videoRef.current.paused) return;
    inactivityTimerRef.current = setTimeout(() => {
      setShowVideoUI(false);
    }, 1800);
  }, []);

  const resetVideoState = () => {
    setIsPlaying(true);
    setIsMuted(true);
    setVolume(0.6);
    setShowVideoUI(true);
    clearInactivityTimer();

    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.volume = 0.6;
      videoRef.current.currentTime = 0;
      videoRef.current
        .play()
        .then(() => {
          startInactivityTimer();
        })
        .catch(() => {});
    }
  };

  const goTo = (index) => {
    if (index === current) return;
    clearVideoEndTimeout();
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
    resetVideoState();
  };

  const goNext = () => {
    clearVideoEndTimeout();
    lastInteractionRef.current = "arrow";
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
    resetVideoState();
  };

  const goPrev = () => {
    clearVideoEndTimeout();
    lastInteractionRef.current = "arrow";
    setDirection(-1);
    setCurrent((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    resetVideoState();
  };

  const handleTogglePlay = () => {
    if (!videoRef.current) return;

    setShowVideoUI(true);

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      startInactivityTimer();
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      clearInactivityTimer();
    }
  };

  const handleToggleMute = () => {
    if (!videoRef.current) return;

    setShowVideoUI(true);
    startInactivityTimer();

    if (videoRef.current.muted) {
      videoRef.current.muted = false;
      setIsMuted(false);
      if (volume === 0) {
        const newVol = 0.6;
        setVolume(newVol);
        videoRef.current.volume = newVol;
      }
    } else {
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    if (!videoRef.current) return;
    const value = parseFloat(e.target.value);
    setVolume(value);
    videoRef.current.volume = value;

    setShowVideoUI(true);
    startInactivityTimer();

    if (value > 0 && videoRef.current.muted) {
      videoRef.current.muted = false;
      setIsMuted(false);
    }
  };

  const audioIsClosed = isMuted;

  useEffect(() => {
    const slide = slides[current];
    if (slide.type !== "image") return;

    const id = setTimeout(() => {
      lastInteractionRef.current = "autoplay";
      goNext();
    }, IMAGE_AUTOPLAY_MS);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const handleVideoEnded = () => {
    clearVideoEndTimeout();
    clearInactivityTimer();
    setShowVideoUI(true);
    videoEndTimeoutRef.current = setTimeout(() => {
      lastInteractionRef.current = "autoplay"; // ⭐
      goNext();
    }, VIDEO_AFTER_END_MS);
  };

  useEffect(() => {
    return () => {
      clearVideoEndTimeout();
      clearInactivityTimer();
    };
  }, []);

  useEffect(() => {
    setShowVideoUI(true);
    clearInactivityTimer();
  }, [current]);

  useEffect(() => {
    const container = thumbsContainerRef.current;
    if (!container) return;

    if (lastInteractionRef.current === "thumb") {
      lastInteractionRef.current = null;
      return;
    }

    if (current === 0) {
      if (container.scrollLeft === 0) {
        return;
      }

      container.scrollTo({
        left: 0,
        behavior: "smooth",
      });
      return;
    }

    const thumb = thumbsRefs.current[current];
    if (!thumb) return;

    const styles = window.getComputedStyle(container);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;

    const SAFE_SPACE = 12;

    let targetLeft = thumb.offsetLeft - paddingLeft - SAFE_SPACE;
    if (targetLeft < 0) targetLeft = 0;

    container.scrollTo({
      left: targetLeft,
      behavior: "smooth",
    });
  }, [current]);

  const handleThumbClick = (index) => {
    const container = thumbsContainerRef.current;
    const thumb = thumbsRefs.current[index];

    lastInteractionRef.current = "thumb";

    if (container && thumb) {
      const containerRect = container.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const margin = 8;

      const isCutLeft = thumbRect.left < containerRect.left + 2;
      const isCutRight = thumbRect.right > containerRect.right - 2;

      let targetLeft = null;

      if (isCutRight) {
        targetLeft = thumb.offsetLeft - margin;
      } else if (isCutLeft) {
        targetLeft =
          thumb.offsetLeft -
          (container.clientWidth - thumb.offsetWidth) +
          margin;
      }

      if (targetLeft !== null) {
        container.scrollTo({
          left: targetLeft,
          behavior: "smooth",
        });
      }
    }

    goTo(index);
  };

  useEffect(() => {
    const handleGlobalMouseMove = () => {
      const slide = slides[current];
      if (!slide || slide.type !== "video") return;
      setShowVideoUI(true);
      startInactivityTimer();
    };

    window.addEventListener("mousemove", handleGlobalMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [current, startInactivityTimer]);

  const handlePillarsDotClick = (page) => {
    setPillarsPage(page);
    setPillarsTimerKey((prev) => prev + 1); 
  };

  return (
  <>
    {/* MOBILE (< md) */}
    <div className="block md:hidden">
      <div className="relative text-slate-50 overflow-hidden pt-10 pb-12">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 flex justify-center">
            <div className="w-56 h-56 rounded-full bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35)_0,transparent_60%)] blur-3xl opacity-75 mt-6" />
          </div>
          <div className="absolute bottom-0 left-4 w-40 h-40 rounded-full bg-sky-500/18 blur-3xl" />
          <div className="absolute -top-24 right-0 w-40 h-40 rounded-full bg-fuchsia-500/28 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.9)_0,_transparent_58%)]" />
        </div>

        <main className="relative max-w-md sm:max-w-lg mx-auto px-4 sm:px-5 pt-4 pb-2 space-y-10">
          {/* HEADER */}
          <header className="text-center">
            <div className="inline-flex relative">
              <motion.div
                className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                animate={{
                  opacity: [0.18, 0.4, 0.18],
                  scale: [0.96, 1.03, 0.96],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.p
                className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                          rounded-full text-[10px] uppercase tracking-[0.3em] 
                          bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scale: [1, 1.25, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                CORE EXPERIENCE
                <motion.span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                  }}
                  animate={{ x: ["-160%", "160%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.p>
            </div>

            <h2
              className="mt-4 text-2xl sm:text-3xl font-extrabold text-transparent 
                        bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                        drop-shadow-[0_0_24px_rgba(129,140,248,0.7)]"
            >
              Explore the Float Block Experience
            </h2>

            <p className="mt-3 text-xs sm:text-sm text-slate-300/85 max-w-sm mx-auto leading-relaxed">
              Discover the core mechanics, visual style, and overall gameplay flow
              through curated images, videos, and detailed insights that highlight
              the heart of the game.
            </p>
          </header>

          {/* CONTENIDO PRINCIPAL */}
          <section className="relative flex flex-col gap-8 items-stretch">
            {/* Carrusel / Video */}
            <div className="space-y-4">
              <motion.div
                className="relative rounded-3xl backdrop-blur-xl 
                          p-2.5 overflow-hidden
                          border border-white/10 bg-white/5"
              >
                <div className="relative aspect-[16/9] overflow-hidden rounded-2xl">
                  {/* Prev */}
                  <button
                    onClick={goPrev}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 inline-flex h-8 w-8 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:-translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duration-300"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {/* Next */}
                  <button
                    onClick={goNext}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 inline-flex h-8 w-8 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duration-300"
                    aria-label="Next slide"
                  >
                    <ChevronRight size={16} />
                  </button>

                  {/* Título de slide */}
                  <div
                    className="absolute top-2.5 left-2.5 z-20 flex flex-col gap-1.5 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    <div className="inline-flex items-center gap-2 rounded-full bg-black/70 px-2.5 py-1 border border-white/15">
                      <span className="text-[11px] font-medium text-slate-100 truncate max-w-[190px]">
                        {activeSlide.title}
                      </span>
                    </div>
                  </div>

                  {/* Botón YouTube */}
                  <div
                    className="absolute top-2.5 right-2.5 z-20 flex flex-col items-end gap-2 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    {activeSlide.type === "video" && activeSlide.youtubeUrl && (
                      <motion.button
                        onClick={() =>
                          window.open(activeSlide.youtubeUrl, "_blank")
                        }
                        className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                                rounded-full text-[10px] uppercase tracking-[0.08em] 
                                bg-red-900/10 text-slate-100 border border-red-900/60 backdrop-blur
                                shadow-[0_0_14px_rgba(0,0,0,0.9)]
                                hover:shadow-[0_0_22px_rgba(120,0,0,1)]
                                transition-shadow duration-300"
                      >
                        <PlayCircle className="w-3.5 h-3.5" />
                        View on Youtube
                        <motion.span
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background:
                              "linear-gradient(120deg, transparent, rgba(255,0,0,0.22), transparent)",
                          }}
                          animate={{ x: ["-160%", "160%"] }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </motion.button>
                    )}
                  </div>

                  {/* Slide */}
                  <AnimatePresence custom={direction} mode="wait" initial={false}>
                    <motion.div
                      key={activeSlide.id + "-" + current}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="absolute inset-0 will-change-transform"
                    >
                      {activeSlide.type === "video" ? (
                        <div className="relative w-full h-full">
                          <video
                            ref={videoRef}
                            className="w-full h-full object-cover object-center"
                            src={activeSlide.src}
                            poster={activeSlide.poster}
                            autoPlay
                            muted={isMuted}
                            playsInline
                            onEnded={handleVideoEnded}
                          />

                          <div
                            className="absolute bottom-2.5 left-2.5 flex items-center gap-2 z-30 transition-opacity duration-300"
                            style={{
                              opacity: showVideoUI ? 1 : 0,
                              pointerEvents: showVideoUI ? "auto" : "none",
                            }}
                          >
                            {/* Play / Pause */}
                            <button
                              type="button"
                              onClick={handleTogglePlay}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full 
                                        bg-black/70 border border-white/15 backdrop-blur-sm
                                        hover:bg-black/90 hover:border-violet-400/70 transition"
                            >
                              {isPlaying ? (
                                <Pause className="w-4 h-4 text-slate-50" />
                              ) : (
                                <Play className="w-4 h-4 text-slate-50" />
                              )}
                            </button>

                            {/* Volumen */}
                            <div className="relative inline-flex items-center h-8 group">
                              <div
                                className={
                                  "absolute inset-y-0 left-0 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm transition-all duration-200 " +
                                  (audioIsClosed ? "w-8" : "w-8 group-hover:w-28")
                                }
                              />
                              <button
                                type="button"
                                onClick={handleToggleMute}
                                className="relative z-10 inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-white/5"
                              >
                                {isMuted || volume === 0 ? (
                                  <VolumeX className="w-4 h-4 text-slate-50" />
                                ) : (
                                  <Volume2 className="w-4 h-4 text-slate-50" />
                                )}
                              </button>

                              <div
                                className={
                                  "relative z-10 pr-1 overflow-hidden transition-all duration-150 " +
                                  (audioIsClosed
                                    ? "w-0 opacity-0"
                                    : "w-0 opacity-0 group-hover:w-20 group-hover:opacity-100")
                                }
                              >
                                <div className="relative h-1 rounded-full bg-slate-700/80">
                                  <div
                                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#a855f7]"
                                    style={{ width: `${volume * 100}%` }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-full h-full">
                          <img
                            src={activeSlide.src}
                            alt={activeSlide.title}
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent" />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Thumbnails */}
                <div
                  className="mt-3 flex gap-2 overflow-x-auto pb-2 px-1.5 pt-3"
                  ref={thumbsContainerRef}
                >
                  {slides.map((slide, index) => {
                    const isActive = index === current;
                    return (
                      <button
                        key={slide.id + "-" + index}
                        ref={(el) => {
                          thumbsRefs.current[index] = el;
                        }}
                        onClick={() => handleThumbClick(index)}
                        className={`relative flex-shrink-0 rounded-xl border overflow-hidden transition-all duration-200 
                                    bg-white/5 backdrop-blur ${
                                      isActive
                                        ? "border-violet-400/90 shadow-[0_0_6px_rgba(79,70,229,0.85)] scale-[1.05]"
                                        : "border-slate-700/60 opacity-80 hover:opacity-100 hover:border-violet-300/70"
                                    }`}
                        style={{ width: 104, height: 64 }}
                      >
                        {slide.type === "video" ? (
                          <>
                            <img
                              src={slide.poster}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <PlayCircle className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        ) : (
                          <>
                            <img
                              src={slide.src}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <ImageIcon className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </div>

            {/* Aside / pilares */}
            <aside className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-violet-200/80">
                    Core Elements
                  </p>
                  <h3 className="mt-1 text-base font-semibold text-slate-50">
                    What defines the game
                  </h3>
                </div>
                <div className="flex flex-col items-end gap-1 text-[10px] text-slate-400">
                  <span>{pillarsPage === 0 ? "01–02" : "03–04"}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(0)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 0
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 01–02"
                    />
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(1)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 1
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 03–04"
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {pillarsPage === 0 ? (
                  <motion.div
                    key="pillars-page-1"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-4 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-[11px] text-slate-400 mb-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Sparkles size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Narrative
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-sm">
                        The Guardian&apos;s Warning
                      </h4>
                      <p className="text-slate-300 text-xs mt-2 leading-relaxed">
                        The Guardian foresaw specters breaking free and a threat
                        called the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_20px_rgba(129,140,248,0.7)]"
                        >
                          Devourer of universes
                        </span>
                        . Now four worlds lie in darkness, and only you can stop its
                        advance.
                      </p>
                    </article>

                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-4 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-[11px] text-slate-400 mb-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Layers size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Levels
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-sm">
                        Puzzles that Evolve in Complexity
                      </h4>

                      <p className="text-slate-300 text-xs mt-2 leading-relaxed">
                        Explore more than 200 handcrafted levels built around{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_20px_rgba(129,140,248,0.7)]"
                        >
                          10 unique mechanics
                        </span>
                        . Each world adds new challenges that increase difficulty
                        and require careful strategy.
                      </p>
                    </article>
                  </motion.div>
                ) : (
                  <motion.div
                    key="pillars-page-2"
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-4 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-[11px] text-slate-400 mb-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Box size={13} className="text-sky-300" />
                          <span className="font-medium tracking-wide">
                            Collection / Skins
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-sm">
                        30 Skins, Chests, and Collectible Rewards
                      </h4>

                      <p className="text-slate-300 text-xs mt-2 leading-relaxed">
                        Unlock{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_20px_rgba(129,140,248,0.7)]"
                        >
                          30 character skins
                        </span>{" "}
                        through progression and chests. Each reward encourages
                        replaying levels, improving performance, and expanding your
                        collection.
                      </p>
                    </article>

                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-4 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-[11px] text-slate-400 mb-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Crown size={13} className="text-fuchsia-300" />
                          <span className="font-medium tracking-wide">
                            Endgame
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-sm">
                        Final Encounter and Procedural Challenge
                      </h4>

                      <p className="text-slate-300 text-xs mt-2 leading-relaxed">
                        Confront the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_20px_rgba(129,140,248,0.7)]"
                        >
                          final boss
                        </span>{" "}
                        and then test your skills in a{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_20px_rgba(129,140,248,0.7)]"
                        >
                          procedural race
                        </span>{" "}
                        against a bot. With layouts that never repeat, the challenge
                        continues indefinitely.
                      </p>
                    </article>
                  </motion.div>
                )}
              </AnimatePresence>
            </aside>
          </section>
        </main>
      </div>
    </div>

    {/* TABLET (md ≤ width < lg) */}
    <div className="hidden md:block lg:hidden">
      <div className="relative text-slate-50 overflow-hidden py-14">
        {/* FONDO / GLOWS */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 flex justify-center">
            <div className="w-72 h-72 rounded-full bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35)_0,transparent_60%)] blur-3xl opacity-80 mt-6" />
          </div>
          <div className="absolute bottom-[-40px] left-6 w-60 h-60 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute -top-28 right-[-20px] w-56 h-56 rounded-full bg-fuchsia-500/32 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.9)_0,_transparent_58%)]" />
        </div>

        <main className="relative max-w-4xl mx-auto px-6 py-14 space-y-10">
          {/* HEADER */}
          <header className="text-center">
            <div className="inline-flex relative">
              <motion.div
                className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                animate={{
                  opacity: [0.18, 0.4, 0.18],
                  scale: [0.96, 1.03, 0.96],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.p
                className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                          rounded-full text-[11px] uppercase tracking-[0.35em] 
                          bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scale: [1, 1.25, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                CORE EXPERIENCE
                <motion.span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                  }}
                  animate={{ x: ["-160%", "160%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.p>
            </div>

            <h2
              className="mt-5 text-3xl md:text-[2.1rem] font-extrabold text-transparent 
                        bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                        drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
            >
              Explore the Float Block Experience
            </h2>

            <p className="mt-4 text-sm md:text-base text-slate-300/85 max-w-xl mx-auto leading-relaxed">
              Discover the core mechanics, visual style, and overall gameplay flow through curated
              images, videos, and detailed insights that highlight the heart of the game.
            </p>
          </header>

          {/* AQUÍ: GALERÍA + CORE ELEMENTS EN COLUMNA */}
          <section className="relative flex flex-col gap-10 items-stretch">
            {/* GALERÍA / CARRUSEL */}
            <div className="space-y-4">
              <motion.div
                className="relative rounded-[1.75rem] backdrop-blur-xl 
                          p-3 overflow-hidden
                          border border-white/10 bg-white/5"
              >
                <div className="relative aspect-[16/9] overflow-hidden rounded-2xl">
                  {/* Prev */}
                  <button
                    onClick={goPrev}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-9 w-9 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:-translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duration-300"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  {/* Next */}
                  <button
                    onClick={goNext}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-9 w-9 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duration-300"
                    aria-label="Next slide"
                  >
                    <ChevronRight size={18} />
                  </button>

                  {/* Título de slide */}
                  <div
                    className="absolute top-3 left-3 z-20 flex flex-col gap-2 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    <div className="inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-1 border border-white/15">
                      <span className="text-xs font-medium text-slate-100 truncate max-w-[260px]">
                        {activeSlide.title}
                      </span>
                    </div>
                  </div>

                  {/* Botón YouTube */}
                  <div
                    className="absolute top-3 right-3 z-20 flex flex-col items-end gap-2 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    {activeSlide.type === "video" && activeSlide.youtubeUrl && (
                      <motion.button
                        onClick={() => window.open(activeSlide.youtubeUrl, "_blank")}
                        className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                                rounded-full text-[11px] uppercase tracking-[0.08em] 
                                bg-red-900/10 text-slate-100 border border-red-900/60 backdrop-blur
                                shadow-[0_0_14px_rgba(0,0,0,0.9)]
                                hover:shadow-[0_0_22px_rgba(120,0,0,1)]
                                transition-shadow duration-300"
                      >
                        <PlayCircle className="w-3.5 h-3.5" />
                        View on Youtube
                        <motion.span
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background:
                              "linear-gradient(120deg, transparent, rgba(255,0,0,0.22), transparent)",
                          }}
                          animate={{ x: ["-160%", "160%"] }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        />
                      </motion.button>
                    )}
                  </div>

                  {/* SLIDE */}
                  <AnimatePresence custom={direction} mode="wait" initial={false}>
                    <motion.div
                      key={activeSlide.id + "-" + current}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="absolute inset-0 will-change-transform"
                    >
                      {activeSlide.type === "video" ? (
                        <div className="relative w-full h-full">
                          <video
                            ref={videoRef}
                            className="w-full h-full object-cover object-center"
                            src={activeSlide.src}
                            poster={activeSlide.poster}
                            autoPlay
                            muted={isMuted}
                            playsInline
                            onEnded={handleVideoEnded}
                          />

                          <div
                            className="absolute bottom-3 left-3 flex items-center gap-2 z-30 transition-opacity duration-300"
                            style={{
                              opacity: showVideoUI ? 1 : 0,
                              pointerEvents: showVideoUI ? "auto" : "none",
                            }}
                          >
                            {/* Play / Pause */}
                            <button
                              type="button"
                              onClick={handleTogglePlay}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full 
                                        bg-black/70 border border-white/15 backdrop-blur-sm
                                        hover:bg-black/90 hover:border-violet-400/70 transition"
                            >
                              {isPlaying ? (
                                <Pause className="w-4 h-4 text-slate-50" />
                              ) : (
                                <Play className="w-4 h-4 text-slate-50" />
                              )}
                            </button>

                            {/* Volumen */}
                            <div className="relative inline-flex items-center h-9 group">
                              <div
                                className={
                                  "absolute inset-y-0 left-0 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm transition-all duration-200 " +
                                  (audioIsClosed ? "w-9" : "w-9 group-hover:w-32")
                                }
                              />
                              <button
                                type="button"
                                onClick={handleToggleMute}
                                className="relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/5"
                              >
                                {isMuted || volume === 0 ? (
                                  <VolumeX className="w-4 h-4 text-slate-50" />
                                ) : (
                                  <Volume2 className="w-4 h-4 text-slate-50" />
                                )}
                              </button>

                              <div
                                className={
                                  "relative z-10 pr-1 overflow-hidden transition-all duration-150 " +
                                  (audioIsClosed
                                    ? "w-0 opacity-0"
                                    : "w-0 opacity-0 group-hover:w-20 group-hover:opacity-100")
                                }
                              >
                                <div className="relative h-1 rounded-full bg-slate-700/80">
                                  <div
                                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#a855f7]"
                                    style={{ width: `${volume * 100}%` }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-full h-full">
                          <img
                            src={activeSlide.src}
                            alt={activeSlide.title}
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent" />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* THUMBNAILS */}
                <div
                  className="mt-3 flex gap-2 overflow-x-auto pb-2 px-2 pt-4"
                  ref={thumbsContainerRef}
                >
                  {slides.map((slide, index) => {
                    const isActive = index === current;
                    return (
                      <button
                        key={slide.id + "-" + index}
                        ref={(el) => {
                          thumbsRefs.current[index] = el;
                        }}
                        onClick={() => handleThumbClick(index)}
                        className={`relative flex-shrink-0 rounded-xl border overflow-hidden transition-all duration-200 
                                    bg-white/5 backdrop-blur ${
                                      isActive
                                        ? "border-violet-400/90 shadow-[0_0_6px_rgba(79,70,229,0.85)] scale-[1.05]"
                                        : "border-slate-700/60 opacity-80 hover:opacity-100 hover:border-violet-300/70"
                                    }`}
                        style={{ width: 120, height: 72 }}
                      >
                        {slide.type === "video" ? (
                          <>
                            <img
                              src={slide.poster}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <PlayCircle className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        ) : (
                          <>
                            <img
                              src={slide.src}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <ImageIcon className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </div>

            {/* CORE ELEMENTS DEBAJO DE LA GALERÍA */}
            <aside className="space-y-4">
              <div className="flex items-center justify-between gap-4 mb-1">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-violet-200/80">
                    Core Elements
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-50">
                    What defines the game
                  </h3>
                </div>
                <div className="flex flex-col items-end gap-1 text-[11px] text-slate-400">
                  <span>{pillarsPage === 0 ? "01–02" : "03–04"}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(0)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 0
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 01–02"
                    />
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(1)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 1
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 03–04"
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {pillarsPage === 0 ? (
                  <motion.div
                    key="pillars-page-1"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Sparkles size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Narrative
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base">
                        The Guardian&apos;s Warning
                      </h4>
                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        The Guardian foresaw specters breaking free and a threat
                        called the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          Devourer of universes
                        </span>
                        . Now four worlds lie in darkness, and only you can stop its
                        advance.
                      </p>
                    </article>

                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Layers size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Levels
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base">
                        Puzzles that Evolve in Complexity
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Explore more than 200 handcrafted levels built around{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          10 unique mechanics
                        </span>
                        . Each world adds new challenges that increase difficulty
                        and require careful strategy.
                      </p>
                    </article>
                  </motion.div>
                ) : (
                  <motion.div
                    key="pillars-page-2"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Box size={13} className="text-sky-300" />
                          <span className="font-medium tracking-wide">
                            Collection / Skins
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base">
                        30 Skins, Chests, and Collectible Rewards
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Unlock{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          30 character skins
                        </span>{" "}
                        through progression and chests. Each reward encourages
                        replaying levels, improving performance, and expanding your
                        collection.
                      </p>
                    </article>

                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 h-auto overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Crown size={13} className="text-fuchsia-300" />
                          <span className="font-medium tracking-wide">
                            Endgame
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base">
                        Final Encounter and Procedural Challenge
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Confront the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          final boss
                        </span>{" "}
                        and then test your skills in a{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          procedural race
                        </span>{" "}
                        against a bot. With layouts that never repeat, the challenge
                        continues indefinitely.
                      </p>
                    </article>
                  </motion.div>
                )}
              </AnimatePresence>
            </aside>
          </section>
        </main>
      </div>
    </div>

    {/* DESKTOP (lg+) */}
    <div className="hidden lg:block">
      <div className="relative text-slate-50 overflow-hidden py-14">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 flex justify-center">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-full bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35)_0,transparent_60%)] blur-3xl opacity-75 mt-4" />
          </div>
          <div className="absolute bottom-0 left-10 w-56 h-56 rounded-full bg-sky-500/18 blur-3xl" />
          <div className="absolute -top-32 right-0 w-52 h-52 rounded-full bg-fuchsia-500/28 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.85)_0,_transparent_58%)]" />
        </div>

        <main className="relative max-w-6xl mx-auto px-6 py-16 lg:py-20 space-y-12 lg:space-y-16">
          <header className="text-center">
            <div className="inline-flex relative">
              <motion.div
                className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                animate={{
                  opacity: [0.18, 0.4, 0.18],
                  scale: [0.96, 1.03, 0.96],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.p
                className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                          rounded-full text-[11px] uppercase tracking-[0.35em] 
                          bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scale: [1, 1.25, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                CORE EXPERIENCE
                <motion.span
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                  }}
                  animate={{ x: ["-160%", "160%"] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.p>
            </div>

            <h2
              className="mt-6 text-3xl md:text-4xl font-extrabold text-transparent 
                        bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                        drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
            >
              Explore the Float Block Experience
            </h2>

            <p className="mt-4 text-sm md:text-base text-slate-300/85 max-w-2xl mx-auto">
              Discover the core mechanics, visual style, and overall gameplay flow through curated <br />
              images, videos, and detailed insights that highlight the heart of the game
            </p>
          </header>

          <section className="relative grid gap-10 lg:gap-12 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1.1fr)] items-start">
            <div className="space-y-4">
              <motion.div
                className="relative rounded-[1.75rem] backdrop-blur-xl 
                          p-2.5 sm:p-3 overflow-hidden
                          border border-white/10 bg-white/5"
              >
                <motion.div />

                <div className="relative aspect-video overflow-hidden rounded-2xl">
                  <button
                    onClick={goPrev}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-9 w-9 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:-translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duration-300"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <button
                    onClick={goNext}
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-20 inline-flex h-9 w-9 items-center justify-center 
                              rounded-full bg-slate-950/95 border border-slate-700/80 text-slate-100/90 
                              hover:bg-slate-900 hover:translate-x-0.5 hover:scale-105 
                              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70 transition-opacity duración-300"
                    aria-label="Next slide"
                  >
                    <ChevronRight size={18} />
                  </button>

                  <div
                    className="absolute top-3 left-3 z-20 flex flex-col gap-2 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    <div className="inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-1 border border-white/15">
                      <span className="text-xs font-medium text-slate-100 truncate max-w-[210px] sm:max-w-[260px]">
                        {activeSlide.title}
                      </span>
                    </div>
                  </div>

                  <div
                    className="absolute top-3 right-3 z-20 flex flex-col items-end gap-2 transition-opacity duration-300"
                    style={{
                      opacity: showVideoUI ? 1 : 0,
                      pointerEvents: showVideoUI ? "auto" : "none",
                    }}
                  >
                    {activeSlide.type === "video" &&
                      activeSlide.youtubeUrl && (
                        <motion.button
                          onClick={() =>
                            window.open(activeSlide.youtubeUrl, "_blank")
                          }
                          className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                                  rounded-full text-[11px] uppercase tracking-[0.08em] 
                                  bg-red-900/10 text-slate-100 border border-red-900/60 backdrop-blur
                                  shadow-[0_0_14px_rgba(0,0,0,0.9)]
                                  hover:shadow-[0_0_22px_rgba(120,0,0,1)]
                                  transition-shadow duración-300"
                        >
                          <PlayCircle className="w-3.5 h-3.5" />
                          View on Youtube
                          <motion.span
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(120deg, transparent, rgba(255,0,0,0.22), transparent)",
                            }}
                            animate={{ x: ["-160%", "160%"] }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                        </motion.button>
                      )}
                  </div>

                  <AnimatePresence custom={direction} mode="wait" initial={false}>
                    <motion.div
                      key={activeSlide.id + "-" + current}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="absolute inset-0 will-change-transform"
                    >
                      {activeSlide.type === "video" ? (
                        <div className="relative w-full h-full">
                          <video
                            ref={videoRef}
                            className="w-full h-full object-cover object-center"
                            src={activeSlide.src}
                            poster={activeSlide.poster}
                            autoPlay
                            muted={isMuted}
                            playsInline
                            onEnded={handleVideoEnded}
                          />

                          <div
                            className="absolute bottom-3 left-3 flex items-center gap-2 z-30 transition-opacity duration-300"
                            style={{
                              opacity: showVideoUI ? 1 : 0,
                              pointerEvents: showVideoUI ? "auto" : "none",
                            }}
                          >
                            <button
                              type="button"
                              onClick={handleTogglePlay}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full 
                                        bg-black/70 border border-white/15 backdrop-blur-sm
                                        hover:bg-black/90 hover:border-violet-400/70 transition"
                            >
                              {isPlaying ? (
                                <Pause className="w-4 h-4 text-slate-50" />
                              ) : (
                                <Play className="w-4 h-4 text-slate-50" />
                              )}
                            </button>

                            <div className="relative inline-flex items-center h-9 group">
                              <div
                                className={
                                  "absolute inset-y-0 left-0 rounded-full bg-black/70 border border-white/15 backdrop-blur-sm transition-all duración-200 " +
                                  (audioIsClosed
                                    ? "w-9"
                                    : "w-9 group-hover:w-32")
                                }
                              />
                              <button
                                type="button"
                                onClick={handleToggleMute}
                                className="relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/5"
                              >
                                {isMuted || volume === 0 ? (
                                  <VolumeX className="w-4 h-4 text-slate-50" />
                                ) : (
                                  <Volume2 className="w-4 h-4 text-slate-50" />
                                )}
                              </button>

                              <div
                                className={
                                  "relative z-10 pr-1 overflow-hidden transición-all duración-150 " +
                                  (audioIsClosed
                                    ? "w-0 opacity-0"
                                    : "w-0 opacity-0 group-hover:w-20 group-hover:opacity-100")
                                }
                              >
                                <div className="relative h-1 rounded-full bg-slate-700/80">
                                  <div
                                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#a855f7]"
                                    style={{ width: `${volume * 100}%` }}
                                  />
                                  <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    value={volume}
                                    onChange={handleVolumeChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-full h-full">
                          <img
                            src={activeSlide.src}
                            alt={activeSlide.title}
                            className="w-full h-full object-cover object-center"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent" />
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div
                  className="mt-3 flex gap-2 overflow-x-auto pb-1 px-2 pt-4"
                  ref={thumbsContainerRef}
                >
                  {slides.map((slide, index) => {
                    const isActive = index === current;
                    return (
                      <button
                        key={slide.id + "-" + index}
                        ref={(el) => {
                          thumbsRefs.current[index] = el;
                        }}
                        onClick={() => handleThumbClick(index)}
                        className={`relative flex-shrink-0 rounded-xl border overflow-hidden transition-all duration-200 
                                    bg-white/5 backdrop-blur ${
                                      isActive
                                        ? "border-violet-400/90 shadow-[0_0_6px_rgba(79,70,229,0.85)] scale-[1.05]"
                                        : "border-slate-700/60 opacity-80 hover:opacity-100 hover:border-violet-300/70"
                                    }`}
                        style={{ width: 120, height: 72 }}
                      >
                        {slide.type === "video" ? (
                          <>
                            <img
                              src={slide.poster}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <PlayCircle className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        ) : (
                          <>
                            <img
                              src={slide.src}
                              alt={slide.title}
                              className="w-full h-full object-cover object-center"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-transparent" />
                            <ImageIcon className="absolute bottom-1.5 right-1.5 w-4 h-4 text-slate-50/85 drop-shadow" />
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </div>

            <aside className="lg:sticky lg:top-28 space-y-4">
              <div className="flex items-center justify-between gap-4 mb-1">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-violet-200/80">
                    Core Elements
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-50">
                    What defines the game
                  </h3>
                </div>
                <div className="flex flex-col items-end gap-1 text-[11px] text-slate-400">
                  <span>{pillarsPage === 0 ? "01–02" : "03–04"}</span>
                  <div className="flex items-center gap-1.5">
                    {/* 🔘 Dots clicables que reinician el contador */}
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(0)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 0
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 01–02"
                    />
                    <button
                      type="button"
                      onClick={() => handlePillarsDotClick(1)}
                      className={
                        "h-1.5 w-3 rounded-full transition-all " +
                        (pillarsPage === 1
                          ? "bg-violet-400"
                          : "bg-slate-600/70 hover:bg-slate-400")
                      }
                      aria-label="Show pillars 03–04"
                    />
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait">
                {pillarsPage === 0 ? (
                  <motion.div
                    key="pillars-page-1"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 md:p-6 h-auto lg:h-[200px] overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Sparkles size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Narrative
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base md:text-lg">
                        The Guardian&apos;s Warning
                      </h4>
                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        The Guardian foresaw specters breaking free and a threat
                        called the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          Devourer of universes
                        </span>
                        .{" "}
                        Now four worlds lie in darkness, and only you can stop
                        its advance.
                      </p>
                    </article>

                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 md:p-6 h-auto lg:h-[200px] overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Layers size={13} className="text-violet-300" />
                          <span className="font-medium tracking-wide">
                            Levels
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base md:text-lg">
                        Puzzles that Evolve in Complexity
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Explore more than 200 handcrafted levels built around{" "}
                        <br />
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          10 unique mechanics
                        </span>
                        .{" "}
                        Each world adds new challenges that increase difficulty
                        and require careful strategy.
                      </p>
                    </article>
                  </motion.div>
                ) : (
                  <motion.div
                    key="pillars-page-2"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="space-y-3"
                  >
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 md:p-6 h-auto lg:h-[200px] overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Box size={13} className="text-sky-300" />
                          <span className="font-medium tracking-wide">
                            Collection / Skins
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base md:text-lg">
                        30 Skins, Chests, and Collectible Rewards
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Unlock{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          30 character skins{" "}
                        </span>
                        through progression and chests. Each reward encourages
                        replaying levels, improving performance, and expanding
                        your collection.
                      </p>
                    </article>
                    
                    <article
                      className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl 
                                p-5 md:p-6 h-auto lg:h-[200px] overflow-hidden"
                    >
                      <div className="flex items-start justify-between gap-3 text-xs text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-2.5 py-1 border border-slate-600/80">
                          <Crown size={13} className="text-fuchsia-300" />
                          <span className="font-medium tracking-wide">
                            Endgame
                          </span>
                        </span>
                      </div>

                      <h4 className="font-semibold text-violet-100 text-base md:text-lg">
                        Final Encounter and Procedural Challenge
                      </h4>

                      <p className="text-slate-300 text-sm mt-2 leading-relaxed">
                        Confront the{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          final boss{" "}
                        </span>
                        and then test your skills in a{" "}
                        <span
                          className="font-semibold text-transparent 
                                    bg-clip-text bg-gradient-to-r 
                                    from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
                        >
                          procedural race{" "}
                        </span>
                        against a bot. With layouts that never repeat, the
                        challenge continues indefinitely.
                      </p>
                    </article>
                  </motion.div>
                )}
              </AnimatePresence>
            </aside>
          </section>
        </main>
      </div>
    </div>
  </>
  );
}
